import type {
	ICellRendererParams,
	ValueFormatterParams,
} from 'ag-grid-community';

import { renderEventCell } from 'features/events/components/EventTooltip';
import {
	rateLevelFcstValueGetter,
	zeroDecimalFormatter,
} from '../tableCustomCellRender';
import type { SourceColumn } from '../useDashboardColumns';

import { labels } from 'locales/en.label';
import { ColumnSources, DEFAULT_COL_WIDTH } from './types';

/**
 * Generates a forecast level header name
 * @param level - The level letter (A, B, C, D, E, F, G, H)
 * @returns Formatted header string for forecast levels
 */
const getForecastLevelHeader = (level: string): string => {
	return `Level ${level.toUpperCase()}\nOTB / Fcst\n Status / Rec Action`;
};

export const forecastColsData: SourceColumn[] = [
	{
		headerName: 'Event',
		field: 'event_count',
		headerTooltip: 'Number of events scheduled for this day',
		maxWidth: DEFAULT_COL_WIDTH - 30,
		valueFormatter: (params: ValueFormatterParams) => {
			return params?.value || 0;
		},
		cellRenderer: renderEventCell,
		source: ColumnSources.FORECAST,
	},
	{
		headerName: 'Transient\nFcst',
		field: 'transient_fcst',
		headerTooltip: labels.rec_review.dashboard.tooltip.transient_fcst,
		maxWidth: DEFAULT_COL_WIDTH - 10,
		valueFormatter: (params: ValueFormatterParams) => {
			return zeroDecimalFormatter(params?.value);
		},
		source: ColumnSources.FORECAST,
	},
	{
		headerName: 'Forecast\nOverride',
		field: 'transient_fcst_event',
		headerTooltip: labels.rec_review.dashboard.tooltip.transient_fcst_event,
		maxWidth: DEFAULT_COL_WIDTH - 10,
		valueFormatter: (params: ValueFormatterParams) => {
			return zeroDecimalFormatter(params?.value);
		},
		source: ColumnSources.FORECAST,
	},
	{
		headerName: 'Unc Transient Fcst',
		field: 'unc_transient_fcst',
		headerTooltip: labels.rec_review.dashboard.tooltip.unc_transient_fcst,
		source: ColumnSources.FORECAST,
	},
	{
		headerName: getForecastLevelHeader('A'),
		field: 'a_fcst_sell',
		headerTooltip: labels.rec_review.dashboard.tooltip.fcst_lvl_rate,
		cellRenderer: (params: ICellRendererParams) =>
			rateLevelFcstValueGetter(params, 'a'),
		source: ColumnSources.FORECAST,
		maxWidth: DEFAULT_COL_WIDTH + 35,
	},
	{
		headerName: getForecastLevelHeader('B'),
		field: 'b_fcst_sell',
		headerTooltip: labels.rec_review.dashboard.tooltip.fcst_lvl_rate,
		cellRenderer: (params: ICellRendererParams) =>
			rateLevelFcstValueGetter(params, 'b'),
		source: ColumnSources.FORECAST,
		maxWidth: DEFAULT_COL_WIDTH + 35,
	},
	{
		headerName: getForecastLevelHeader('C'),
		field: 'c_fcst_sell',
		headerTooltip: labels.rec_review.dashboard.tooltip.fcst_lvl_rate,
		cellRenderer: (params: ICellRendererParams) =>
			rateLevelFcstValueGetter(params, 'c'),
		source: ColumnSources.FORECAST,
		maxWidth: DEFAULT_COL_WIDTH + 35,
	},
	{
		headerName: getForecastLevelHeader('D'),
		field: 'd_fcst_sell',
		headerTooltip: labels.rec_review.dashboard.tooltip.fcst_lvl_rate,
		cellRenderer: (params: ICellRendererParams) =>
			rateLevelFcstValueGetter(params, 'd'),
		source: ColumnSources.FORECAST,
		maxWidth: DEFAULT_COL_WIDTH + 35,
	},
	{
		headerName: getForecastLevelHeader('E'),
		field: 'e_fcst_sell',
		headerTooltip: labels.rec_review.dashboard.tooltip.fcst_lvl_rate,
		cellRenderer: (params: ICellRendererParams) =>
			rateLevelFcstValueGetter(params, 'e'),
		source: ColumnSources.FORECAST,
		maxWidth: DEFAULT_COL_WIDTH + 35,
	},
	{
		headerName: getForecastLevelHeader('F'),
		field: 'f_fcst_sell',
		headerTooltip: labels.rec_review.dashboard.tooltip.fcst_lvl_rate,
		cellRenderer: (params: ICellRendererParams) =>
			rateLevelFcstValueGetter(params, 'f'),
		source: ColumnSources.FORECAST,
		maxWidth: DEFAULT_COL_WIDTH + 35,
	},
	{
		headerName: getForecastLevelHeader('G'),
		field: 'g_fcst_sell',
		headerTooltip: labels.rec_review.dashboard.tooltip.fcst_lvl_rate,
		cellRenderer: (params: ICellRendererParams) =>
			rateLevelFcstValueGetter(params, 'g'),
		source: ColumnSources.FORECAST,
		maxWidth: DEFAULT_COL_WIDTH + 35,
	},
	{
		headerName: getForecastLevelHeader('H'),
		field: 'h_fcst_sell',
		headerTooltip: labels.rec_review.dashboard.tooltip.fcst_lvl_rate,
		cellRenderer: (params: ICellRendererParams) =>
			rateLevelFcstValueGetter(params, 'h'),
		source: ColumnSources.FORECAST,
		maxWidth: DEFAULT_COL_WIDTH + 35,
	},
];
